import { msg } from "../../locales/translate";
import { countryEntity, crmEntityType, isOverviewEntity } from "../../modules/overviewAnalysis/entities.mjs";
import { GD } from "src/GD";
import type { OverviewBankEntity } from "./useOverviewAnalysis";

export const getRankingAnalysisEntity = (title: string, label: string, language: string): OverviewBankEntity | undefined => {
	const type = title === "Top countries" ? "country" : title === "Top banks" ? "bank" : title === "Top emails" ? "email" : undefined;
	if (!type) return undefined;
	if (type === "country") {
		const entity = countryEntity(label);
		if (!entity) return undefined;
		try { entity.label = new Intl.DisplayNames([language], { type: "region" }).of(entity.value === "UK" ? "GB" : entity.value) ?? entity.label; } catch { return entity; }
		return entity;
	}
	const entity = { type, value: label, label };
	return isOverviewEntity(entity) ? entity : undefined;
};

export interface BankAnalysisRequest {
	bank: OverviewBankEntity;
	from: string;
	to: string;
	currency: string;
	userId: number;
	projectId: number;
}

export type EntityAnalysisRequest = Omit<BankAnalysisRequest, "bank"> & {
	bank: BankAnalysisRequest["bank"] | { type: "card"; value: string; label: string };
};

export class BankAnalysisError extends Error {
	constructor(message: string, public readonly raw?: string) { super(message); }
}

// CRM responses may contain payment details: keep them only in this tab's memory.
const cache = new Map<string, { expiresAt: number; raw: string }>();
const pending = new Map<string, Promise<string>>();
const CACHE_MS = 2 * 60_000;
const MAX_ENTRIES = 12;
const MAX_CACHED_RESPONSE_LENGTH = 1_000_000;
let epoch = 0;
let subscribed = false;

export const loadBankAnalysis = (input: EntityAnalysisRequest, refresh = false): Promise<string> => loadAnalysisResponse(input, "overview/analyze", refresh);

// Preserve the source response for the Raw tab; parse display data separately.
export const loadEmailInfo = (input: BankAnalysisRequest, refresh = false): Promise<string> => loadAnalysisResponse(input, "overview/email/info", refresh);

const loadAnalysisResponse = (input: EntityAnalysisRequest, method: "overview/analyze" | "overview/email/info", refresh: boolean): Promise<string> => {
	if (!subscribed) {
		subscribed = true;
		GD.S_AUTH_LOG_OUT.subscribe(() => { epoch++; cache.clear(); pending.clear(); });
	}
	const { bank, from, to, currency, userId, projectId } = input;
	const key = JSON.stringify([method, userId, projectId, bank.type, bank.value, from, to, currency]);
	const existing = pending.get(key);
	if (existing) return existing;
	const requestEpoch = epoch;
	let expired = false;
	let timer: ReturnType<typeof setTimeout>;
	const assertContext = async () => {
		const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
		if (expired || requestEpoch !== epoch || profile?.id !== userId || project?.id !== projectId) throw new BankAnalysisError(msg("message_a30c028a"));
	};
	const work = (async () => {
		if (method === "overview/email/info" && bank.type !== "email") throw new BankAnalysisError(msg("email"));
		const validCard = bank.type === "card" && typeof bank.value === "string" && bank.value.trim().length > 0 && bank.value.length <= 256 && !/[\u0000-\u001f\u007f]/.test(bank.value);
		if (!isOverviewEntity(bank) && !validCard) throw new BankAnalysisError(msg("message_2c57f5cd"));
		await assertContext();
		const hit = cache.get(key);
		if (!refresh && hit && hit.expiresAt > Date.now()) return hit.raw;
		cache.delete(key);
		const response = await GD.REQ_API.request({
			method, apiVersion: 2,
			expectedProjectId: projectId,
			headers: { ignoreBusy: "true" },
			data: { ...(method === "overview/email/info" ? { email: bank.value } : { entity_type: bank.type === "card" ? "card" : crmEntityType(bank), entity_value: bank.value }), from, to, ...(currency ? { currency } : {}) },
		});
		await assertContext();
		if (response?.error) throw new BankAnalysisError(msg("crm"), JSON.stringify(response, null, 2));
		if (!response || response.data === undefined) throw new BankAnalysisError(msg("crm_bb682e6c"));
		const raw = JSON.stringify(response, null, 2);
		if (raw.length <= MAX_CACHED_RESPONSE_LENGTH) {
			cache.set(key, { raw, expiresAt: Date.now() + CACHE_MS });
			while (cache.size > MAX_ENTRIES) cache.delete(cache.keys().next().value!);
		}
		return raw;
	})();
	const timeout = new Promise<never>((_, reject) => {
		timer = setTimeout(() => { expired = true; reject(new BankAnalysisError(msg("crm_dc5e6e39"))); }, 45_000);
	});
	const request = Promise.race([work, timeout]).finally(() => { clearTimeout(timer); });
	pending.set(key, request);
	void request.finally(() => { if (pending.get(key) === request) pending.delete(key); }).catch(() => undefined);
	return request;
};
